import React, { useEffect, useState } from 'react';
import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import TeacherSlugLine from '../shared/teacher_slug_line';
import { sort_rows, toggle_sort } from '../shared/use_sortable';
import FinalPayable from './final_payable';
import SessionDrilldownModal from './session_drilldown_modal';
import ShadowBanner from './shadow_banner';
import WhyPopover from './why_popover';
import { fetch_l2 } from '../api';
import { fmt, is_permission_error, load_failed_text, needs_review_reason } from '../format';
import { NEEDS_REVIEW_TEXT, l2_view } from './adapters';
import { DRILLDOWN_KIND } from './params';
import { audit_link } from '../audit/params';

/*
 * PROD-7828 R5 — Overview Level 2: one teacher's breakdown for the pay month.
 * Four category rows (with Waivers, D-3/R5 brief), incentives, additional-payment entries
 * (R8 brief §5.4), final payable (D-7) and the recalc computation history.
 * Read-only throughout: R5 writes nothing (D-1).
 */
const SUB_TABS = [
  { key: 'summary', label: 'Payment summary' },
  { key: 'incentives', label: 'Incentives & additions' },
  { key: 'final', label: 'Final payable' },
  { key: 'history', label: 'Computation history' }
];

const OverviewLevel2 = ({ pay_month, month_label, teacher, on_back, on_error }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [load_error, setLoadError] = useState('');
  const [sub_tab, setSubTab] = useState('summary');
  const [drill, setDrill] = useState(null);
  const [summary_sort, setSummarySort] = useState(null);
  // HM2-M1: bumped on every fetch (incl. retry) so a stale response can never land after a newer one.
  const [reload_token, setReloadToken] = useState(0);

  const teacher_uuid = teacher && teacher.teacher_uuid;

  useEffect(() => {
    if (!teacher_uuid || !pay_month) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetch_l2({ teacher_uuid, pay_month })
      .then((res) => {
        if (cancelled) return;
        setData(l2_view(res));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        // Permission errors also toast (module-wide gate); other failures are inline-only, matching Penalties.
        if (is_permission_error(e)) on_error(e, 'Failed to load the teacher payment breakdown.');
        setData(null);
        setLoading(false);
        // CD-144: shows the server's own LOOKUP_FAILED sentence when the body carries one.
        setLoadError(load_failed_text(e, 'Failed to load the teacher payment breakdown.'));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacher_uuid, pay_month, on_error, reload_token]);

  const back_button = (
    <button type="button" className="tp-btn-out" style={{ marginBottom: 14 }} onClick={on_back}>
      ← Back to all teachers
    </button>
  );

  if (loading) {
    return (
      <div>
        {back_button}
        <CustomLoading />
      </div>
    );
  }
  if (load_error) {
    return (
      <div>
        {back_button}
        <LoadFailed text={load_error} on_retry={() => setReloadToken((n) => n + 1)} />
      </div>
    );
  }
  if (!data) {
    return (
      <div>
        {back_button}
        <div className="tp-empty">No breakdown available for this teacher and month.</div>
      </div>
    );
  }

  const { meta, summary, incentives, additional, history } = data;
  const stale = data.needs_review;

  const open_drill = (row, kind) =>
    setDrill({
      category: row.category,
      category_label: kind === DRILLDOWN_KIND.PENALTIES ? `${row.label} — penalties` : row.label,
      kind
    });

  const num_cell = (value, tone) => <span className={tone}>{fmt(value, 2)}</span>;

  const penalty_drill_target = (r) =>
    r.is_unattributed ? { category: r.category, label: 'Not linked to a session type' } : r;

  const summary_columns = [
    {
      key: 'category',
      label: 'Category',
      hideInCard: true,
      sort_key: 'category',
      sort_type: 'text',
      sort_value: (r) => r.label,
      render: (r) =>
        r.is_unattributed ? (
          <span
            className="tp-strong"
            title="Manual or month-level penalties, and rows computed before penalties carried a session type"
          >
            Penalties not linked to a session type
          </span>
        ) : (
          <span className="tp-strong">{r.label}</span>
        )
    },
    {
      key: 'sessions_count',
      label: 'Sessions',
      sort_key: 'sessions_count',
      sort_type: 'number',
      render: (r) => {
        if (r.is_unattributed) return '—';
        return r.can_drill_items ? (
          <button
            type="button"
            className="tp-link"
            onClick={() => open_drill(r, DRILLDOWN_KIND.ALL)}
          >
            {r.sessions_count}
          </button>
        ) : (
          r.sessions_count
        );
      }
    },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      sort_key: 'amount',
      sort_type: 'number',
      render: (r) => (r.is_unattributed ? '—' : num_cell(r.amount, r.amount_tone))
    },
    {
      key: 'penalty_amount',
      label: 'Penalty',
      align: 'right',
      sort_key: 'penalty_amount',
      sort_type: 'number',
      render: (r) =>
        r.can_drill_penalties ? (
          <button
            type="button"
            className="tp-link tp-neg"
            onClick={() => open_drill(penalty_drill_target(r), DRILLDOWN_KIND.PENALTIES)}
          >
            {fmt(r.penalty_amount, 2)}
          </button>
        ) : (
          num_cell(r.penalty_amount, 'tp-neg')
        )
    },
    {
      key: 'waived_amount',
      label: 'Waivers',
      align: 'right',
      sort_key: 'waived_amount',
      sort_type: 'number',
      render: (r) => num_cell(r.waived_amount, 'tp-pos')
    },
    {
      key: 'correction_amount',
      label: 'Corrections',
      align: 'right',
      sort_key: 'correction_amount',
      sort_type: 'number',
      render: (r) => num_cell(r.correction_amount, r.correction_amount < 0 ? 'tp-neg' : 'tp-pos')
    },
    {
      key: 'free_pass_amount',
      label: 'Free pass',
      align: 'right',
      sort_key: 'free_pass_amount',
      sort_type: 'number',
      render: (r) => num_cell(r.free_pass_amount, 'tp-pos')
    },
    {
      key: 'uncharged_orphan_amount',
      label: 'Uncharged orphans',
      align: 'right',
      sort_key: 'uncharged_orphan_amount',
      sort_type: 'number',
      render: (r) => num_cell(r.uncharged_orphan_amount, 'tp-neg')
    },
    {
      key: 'net_amount',
      label: 'Net',
      align: 'right',
      sort_key: 'net_amount',
      sort_type: 'number',
      render: (r) =>
        r.is_unattributed ? '—' : <span className="tp-strong">{fmt(r.net_amount, 2)}</span>
    }
  ];

  // The unattributed line always stays last, in either sort direction: it is a catch-all bucket
  // (D-3), not a category that competes on the sorted column.
  const sorted_summary_rows = sort_rows(summary.rows, summary_columns, summary_sort);
  const summary_rows = summary.unattributed
    ? [...sorted_summary_rows, { ...summary.unattributed, is_unattributed: true }]
    : sorted_summary_rows;
  const on_summary_sort = (key) => setSummarySort((s) => toggle_sort(s, key));

  return (
    <div>
      {back_button}
      <a className="tp-link" style={{ marginLeft: 12 }} href={audit_link(pay_month, teacher_uuid)}>
        All activity this month
      </a>

      <ShadowBanner show={data.shadow_banner} />

      <div className="tp-meta-grid">
        <div className="tp-meta-field">
          <div className="tp-meta-k">Teacher</div>
          <div className="tp-meta-v">
            {meta.teacher_name || (teacher && teacher.teacher_name) || '--'}
          </div>
          <TeacherSlugLine slug={meta.teacher_slug || (teacher && teacher.teacher_slug)} />
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">Month</div>
          <div className="tp-meta-v">{month_label}</div>
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">Base rate (month snapshot)</div>
          <div className="tp-meta-v">{fmt(meta.base_rate_snapshot)}</div>
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">GST</div>
          <div className="tp-meta-v">
            {meta.is_gst_registered ? 'Registered' : 'Not registered'}
          </div>
        </div>
        {/* Ops Q8/C4.3: retired — the extra-participant category now comes from Profile Live At,
            shown on the teacher's Payment attributes card, not this per-month breakdown. */}
        <div className="tp-meta-field">
          <div className="tp-meta-k">Free Pass balance</div>
          <div className="tp-meta-v">
            {meta.free_pass_balance === null || meta.free_pass_balance === undefined
              ? '--'
              : meta.free_pass_balance}
          </div>
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">Net payable (rounded)</div>
          <div className="tp-meta-v" style={{ color: 'var(--brand-primary)' }}>
            {stale ? (
              <span className="tp-neg">{NEEDS_REVIEW_TEXT}</span>
            ) : (
              fmt(data.rounded_net_payable)
            )}
          </div>
        </div>
      </div>

      {stale ? (
        <div className="tp-banner tp-banner-error">
          ⚠ {needs_review_reason(meta)} — the amounts below are stale until the month recomputes.
        </div>
      ) : null}

      <div className="tp-tabs" style={{ marginBottom: 14 }}>
        {SUB_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`tp-tab ${sub_tab === t.key ? 'on' : ''}`}
            onClick={() => setSubTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {sub_tab === 'summary' ? (
        <div className="tp-card">
          <div className="tp-card-hd">
            <span className="tp-card-hd-title">Payment summary</span>
            <span className="tp-card-hd-sub">
              Click a session count or penalty for session-level detail
            </span>
          </div>
          <DataTable
            columns={summary_columns}
            rows={summary_rows}
            rowKey={(r) => r.category}
            sort={summary_sort}
            on_sort={on_summary_sort}
            emptyText="No payment items for this month."
            cardTitle={(r) =>
              r.is_unattributed ? 'Penalties not linked to a session type' : r.label
            }
          />
        </div>
      ) : null}

      {sub_tab === 'incentives' ? (
        <>
          <div className="tp-card">
            <div className="tp-card-hd">
              <span className="tp-card-hd-title">System-calculated incentives</span>
            </div>
            <div className="tp-tablewrap">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Related session</th>
                  </tr>
                </thead>
                <tbody>
                  {incentives.map((i) => (
                    <tr key={i.key}>
                      <td>{i.category}</td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{i.item_type}</td>
                      <td style={{ textAlign: 'right' }}>
                        <WhyPopover
                          detail={i.explain}
                          amount_node={<span className="tp-pos">{i.amount_text}</span>}
                        />
                      </td>
                      <td>
                        {i.session_uuid ? (
                          <a
                            className="tp-link"
                            href={`/sessiondetails?id=${i.session_uuid}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Session
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                  {incentives.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="tp-empty">
                        No incentives for this month.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
          <div className="tp-card">
            <div className="tp-card-hd">
              <span className="tp-card-hd-title">Additional payments</span>
              <span className="tp-card-hd-sub">
                Inside gross, before GST and TDS (PRD 22.4) · entered on the Additional payments tab
              </span>
            </div>
            <div className="tp-tablewrap">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Date / time (IST)</th>
                    <th>Category</th>
                    <th>Ref</th>
                    <th>Source</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Why</th>
                    <th>Note</th>
                    <th>Added by</th>
                  </tr>
                </thead>
                <tbody>
                  {additional.map((a) => (
                    <tr key={a.key}>
                      <td>{a.type_label}</td>
                      <td style={{ fontSize: 11 }}>{a.date_text}</td>
                      <td>{a.category_label}</td>
                      <td className="tp-mono">{a.reference_id || '—'}</td>
                      <td>{a.source_text}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span className={a.amount_tone}>{a.amount_text}</span>
                      </td>
                      <td>
                        {a.explain && a.explain.rule_ref ? (
                          <WhyPopover
                            detail={a.explain}
                            label="Why this entry?"
                            amount_node={<span>{a.explain.rule_ref}</span>}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }} title={a.note}>
                        {a.note || '—'}
                      </td>
                      <td style={{ fontSize: 11 }} title={a.added_by_title || undefined}>
                        {a.added_by}
                      </td>
                    </tr>
                  ))}
                  {additional.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="tp-empty">
                        No additional payments for this month.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}

      {sub_tab === 'final' ? (
        <FinalPayable lines={data.final_payable} gst_registered={!!meta.is_gst_registered} />
      ) : null}

      {sub_tab === 'history' ? (
        <div className="tp-card">
          <div className="tp-card-hd">
            <span className="tp-card-hd-title">Computation history</span>
            <span className="tp-card-hd-sub">
              Recalc audit rows — every change to this month is explainable
            </span>
          </div>
          <div className="tp-tablewrap">
            <table className="tp-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Trigger</th>
                  <th>Actor</th>
                  <th>Reason</th>
                  {history.show_net ? <th style={{ textAlign: 'right' }}>Net</th> : null}
                </tr>
              </thead>
              <tbody>
                {history.rows.map((h) => (
                  <tr key={h.key}>
                    <td style={{ fontSize: 11 }}>{h.when}</td>
                    <td>{h.action}</td>
                    <td>{h.actor}</td>
                    <td style={{ fontSize: 11 }}>{h.reason}</td>
                    {history.show_net ? (
                      <td style={{ textAlign: 'right' }}>{h.net_text || '—'}</td>
                    ) : null}
                  </tr>
                ))}
                {history.rows.length === 0 ? (
                  <tr>
                    <td colSpan={history.show_net ? 5 : 4} className="tp-empty">
                      No recomputations recorded for this month.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <SessionDrilldownModal
        show={!!drill}
        close={() => setDrill(null)}
        pay_month={pay_month}
        month_label={month_label}
        teacher_uuid={teacher_uuid}
        category={drill ? drill.category : ''}
        category_label={drill ? drill.category_label : ''}
        kind={drill ? drill.kind : ''}
        on_error={on_error}
      />

      {/* Penalty editing, waivers and Free Pass actions live on the Penalties tab (R6). */}
      <div className="tp-card-hd-sub" style={{ marginTop: 10, display: 'block' }}>
        Penalty edits, waivers and email sends are not part of R5 — this page is read-only.
      </div>
    </div>
  );
};

export default OverviewLevel2;
